import { createAsyncThunk } from '@reduxjs/toolkit';
import adserver from 'services/adserver';
import { withErrorHandling } from 'store/wrappers';
import { REDUCER_KEY } from './settlements.consts';
import { SettlementParams } from './settlements.types';
import { selectCurrentCampaignId } from 'store/campaigns';
import { selectCurrentProposalId } from 'store/proposals';

export const fetchAllSettlementsOfProposal = createAsyncThunk(
  REDUCER_KEY + '/fetchAll',
  withErrorHandling(async ({ proposalId }: { proposalId: number }) => {
    const response = await adserver({
      url: `proposal/${proposalId}/settlements`,
      headers: {},
    });
    return response.data.map((settlement: SettlementParams) => ({
      ...settlement,
      proposal: { id: proposalId },
    }));
  }),
);

export const fetchAllSettlementsOfCurrentProposal = createAsyncThunk(
  REDUCER_KEY + '/fetchAllSettlementsOfCurrentProposal',
  withErrorHandling(async (_: any, thunkApi: any) => {
    const proposalId = selectCurrentProposalId(thunkApi.getState());
    const response = await adserver({
      url: `/proposal/${proposalId}/settlements`,
      headers: {},
    });

    return response.data;
  }),
);

export const fetchAllSettlementsOfCampaign = createAsyncThunk(
  REDUCER_KEY + '/fetchAllOfCampaign',
  withErrorHandling(async ({ campaignId }: { campaignId: number }) => {
    const response = await adserver({
      url: `campaign/${campaignId}/settlements`,
      headers: {},
    });
    return response.data;
  }),
);

export const fetchAllSettlementsOfCurrentCampaign = createAsyncThunk(
  REDUCER_KEY + '/fetchAllSettlementsOfCurrentCampaign',
  withErrorHandling(async (_: any, thunkApi: any) => {
    const campaignId = selectCurrentCampaignId(thunkApi.getState());
    const response = await adserver({
      url: `/campaign/${campaignId}/settlements`,
      headers: {},
    });

    return response.data;
  }),
);

export const fetchSettlementById = createAsyncThunk(
  REDUCER_KEY + '/get',
  withErrorHandling(
    async ({
      proposalId,
      settlementId,
    }: {
      proposalId: number;
      settlementId: number;
    }) => {
      const response = await adserver({
        url: `proposal/${proposalId}/settlements/${settlementId}`,
        headers: {},
      });

      return {
        ...response.data,
        proposal: { id: proposalId },
      };
    },
  ),
);

export const submit = createAsyncThunk(
  REDUCER_KEY + '/submit',
  withErrorHandling(async (data: SettlementParams) => {
    const {
      id: settlementId,
      proposal: { id: proposalId },
    } = data;

    const response = await adserver({
      url: `proposal/${proposalId}/settlements/${settlementId}/submit`,
      method: 'POST',
      data,
      headers: {},
    });
    return response.data;
  }),
);

export const accept = createAsyncThunk(
  REDUCER_KEY + '/accept',
  withErrorHandling(async (data: SettlementParams, thunkApi: any) => {
    const {
      id: settlementId,
      proposal: { id: proposalId },
    } = data;
    const response = await adserver({
      url: `proposal/${proposalId}/settlements/${settlementId}/accept`,
      method: 'POST',
      headers: {},
    });
    // last settlement accepted - refresh settlements list
    if (response.data.status === 'FINALIZED') {
      thunkApi.dispatch(
        fetchAllSettlementsOfProposal({ proposalId: proposalId }),
      );
    }
    return response.data;
  }),
);

export const rectify = createAsyncThunk(
  REDUCER_KEY + '/rectify',
  withErrorHandling(async (data: SettlementParams) => {
    const {
      id: settlementId,
      proposal: { id: proposalId },
      commentary,
    } = data;
    const response = await adserver({
      url: `proposal/${proposalId}/settlements/${settlementId}/rectify`,
      method: 'POST',
      headers: {},
      data: { commentary },
    });
    return response.data;
  }),
);

export const reset = createAsyncThunk(
  REDUCER_KEY + '/reset',
  withErrorHandling(async (data: SettlementParams) => {
    const {
      id: settlementId,
      proposal: { id: proposalId },
    } = data;
    const response = await adserver({
      url: `proposal/${proposalId}/settlements/${settlementId}/reset`,
      method: 'POST',
      headers: {},
    });

    return {
      ...response.data,
      proposal: { id: proposalId },
    };
  }),
);

export const removeAllSettlements = (): { type: string } => ({
  type: REDUCER_KEY + '/removeAll',
});
